<template>
<div>
	<div class="consent__field" v-if="lang == 'esp' && showConsent">
		<FormRow>
  		<input type="checkbox" id="consent" name="consent" @click="setValue('custom.consent', $event.target.checked)" />
  		<FormLabel for="consent" class="label__consent">Opta por recibir comunicaciones por SMS.</FormLabel>
		</FormRow>
    <div v-if="getValue('custom.consent')">
      <FormLabel for="consent" class="label__consent">Al marcar esta casilla, doy mi consentimiento para recibir SMS de Orthodox Union. Responde STOP para darte de baja; responde HELP para obtener ayuda. Pueden aplicarse tarifas de mensajes y datos; la frecuencia de los mensajes puede variar. Consulta nuestra política de privacidad y Términos de servicio en <a :href="privacy" target="_blank">{{privacy}}</a>.</FormLabel>
    </div>
	</div>
	<div class="consent__field" v-else-if="lang == 'heb' && showConsent">

    <FormRow>
  		<input type="checkbox" id="consent" name="consent" @click="setValue('custom.consent', $event.target.checked)" />

      <FormLabel for="consent" class="label__consent">
        <span style="margin-right: 5px;">הצטרף לקבלת הודעות SMS</span>
      </FormLabel>
		</FormRow>
    <div v-if="getValue('custom.consent')">
      <FormLabel for="consent" class="label__consent">על ידי סימון תיבה זו, אני מסכים לקבל הודעות SMS מ- Orthodox Union. השב STOP כדי לבטל את הרישום; השב HELP לקבלת תמיכה. ייתכן שיחולו חיובי הודעות ונתונים; תדירות ההודעות עשויה להשתנות. בקר ב-<a :href="privacy" target="_blank">{{privacy}}</a></FormLabel>
	</div>
	</div>
	<div class="consent__field" v-else-if="showConsent">
		<FormRow>
  		<input type="checkbox" id="consent" name="consent" @click="setValue('custom.consent', $event.target.checked)" />

      <FormLabel for="consent" class="label__consent">Opt in to SMS communications</FormLabel>
		</FormRow>
    <div v-if="getValue('custom.consent')">
  		<FormLabel for="consent" class="label__consent">By checking this box, I consent to receive SMS from Orthodox Union. Reply STOP to opt-out; Reply HELP for support; Message & data rates may apply; Messaging frequency may vary. See our privacy policy and Terms of Service at <a :href="privacy" target="_blank">{{privacy}}</a>.</FormLabel>
	</div>
	</div>
</div>
</template>
<script lang="ts">
import { defineComponent, ref, watch, onBeforeUnmount } from 'vue'
import { usePaymentForm } from '~/shared/use-payment-form'
import { PROD_API_URL, STAGE_API_URL } from '~/shared/runtime-environment'

export default defineComponent({
  props: {
    privacy: {
      type: String,
      default: 'https://www.ou.org/privacy-policy',
    },
    lang: {
      type: String
    },
    alive: {
      type: Boolean,
      default: false
    },
  },
  setup() {
    const { setValue, getValue, form } = usePaymentForm()
    const showConsent = ref(true) // Show by default
    const validationTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

    function getPhoneLookupApiBase() {
      if (typeof window !== 'undefined') {
        const explicitApiUrl = (window as any).__PAYMENT_FORM_API_URL__
        const nuxtPublicConfig = (window as any).__NUXT__?.config?.public
        const windowApiUrl = nuxtPublicConfig?.apiUrl || nuxtPublicConfig?.api

        if (typeof explicitApiUrl === 'string' && explicitApiUrl) {
          return explicitApiUrl
        }

        if (typeof windowApiUrl === 'string' && windowApiUrl) {
          return windowApiUrl
        }

        if ((window as any).isProduction === true) {
          return PROD_API_URL
        }
      }

      return STAGE_API_URL
    }

    function getPhoneLookupUrl(phone: string) {
      return `${getPhoneLookupApiBase().replace(/\/$/, '')}/api/phone-lookup?phone=${encodeURIComponent(phone)}`
    }

    // Initialize consent to false by default
    if (getValue('custom.consent') === null || getValue('custom.consent') === undefined) {
      //@ts-ignore

      setValue('custom.consent', false)
    }

    // Function to validate phone number
    async function validatePhone(phone: string) {
      if (!phone || phone.length < 10) {
        // Show consent field for empty or short phone numbers (default state)
        showConsent.value = true
        // Set consent to false by default
        if (getValue('custom.consent') === null || getValue('custom.consent') === undefined) {
          //@ts-ignore
          setValue('custom.consent', false)
        }
        return
      }

      try {
        const response = await fetch(getPhoneLookupUrl(phone))
        const data = await response.json()

        // Check if the phone is mobile from either lineTypeIntelligence or carrier
        const isMobile = (data.lineTypeIntelligence?.type === 'mobile') || (data.carrier?.type === 'mobile')

        if (isMobile && data.valid) {
          // Show consent field for mobile numbers
          showConsent.value = true
          // Set consent to false initially if it's a mobile phone and user hasn't set it yet
          if (getValue('custom.consent') === null || getValue('custom.consent') === undefined) {
            //@ts-ignore
            setValue('custom.consent', false)
          }
        } else if (data.valid) {
          // Hide consent field for non-mobile but valid numbers (landlines, etc.)
          showConsent.value = false
          // Remove consent field entirely if it's not mobile
          setValue('custom.consent', null)
        } else {
          // Show consent field for invalid numbers (default state)
          showConsent.value = true
          // Set consent to false by default
          if (getValue('custom.consent') === null || getValue('custom.consent') === undefined) {
            //@ts-ignore
            setValue('custom.consent', false)
          }
        }
      } catch (error) {
        console.error('Phone validation error:', error)
        // On error, show consent field (default state) and set to false
        showConsent.value = true
        if (getValue('custom.consent') === null || getValue('custom.consent') === undefined) {
          //@ts-ignore
          setValue('custom.consent', false)
        }
      }
    }

    // Debounced validation function
    function debouncedValidatePhone(phone: string) {
      if (validationTimeout.value) {
        clearTimeout(validationTimeout.value)
      }

      validationTimeout.value = setTimeout(() => {
        validatePhone(phone)
      }, 500) // Wait 500ms after user stops typing
    }

    // Watch for phone field changes
    watch(() => form.value.phone, (newPhone) => {
      debouncedValidatePhone(newPhone || '')
    }, { immediate: true })

    // Clean up timeout on component unmount
    onBeforeUnmount(() => {
      if (validationTimeout.value) {
        clearTimeout(validationTimeout.value)
      }
    })

    return {
      form,
      getValue,
      setValue,
      showConsent,
    }
  },
})
</script>
<style scoped lang="scss">
input[type="checkbox"] {
	margin-right: 10px;
  max-width: 15px;
  margin-bottom: 0px;
  &.consent__input{
    margin-right: 0px;
    margin-left: 10px;
  }
}
.consent__field{
	cursor: pointer;
}

::v-deep .form-group {
  display: flex;
}

::v-deep .label__consent{
  line-height: normal;
  margin-bottom: 0px !important;
  padding-bottom: 0px !important;
  font-size: 14px !important;
}
</style>
